import { createStore } from '@/lib/store';
import { toDateInput, toTimeInput } from '@/lib/dates';
import { daysFromToday, formatDate, formatDay, formatTime } from '@/lib/format';
import { nextStepState, staleDays, type NextStepState } from '@shared/deals';
import type { StageColor } from '@shared/types';

// Query keys, labels and small shared state for the deals area.

export interface BoardFilters {
  ownerId?: string;
  mine?: boolean;
  tag?: string;
  q?: string;
  /** Stages listed in full, beyond the first cards. */
  full?: string[];
}

/** Every key sits under a prefix the server refreshes: ['deals'], ['deal', id] or ['fields']. */
export const keys = {
  board: (f: BoardFilters) => ['deals', 'board', f] as const,
  boardAll: ['deals', 'board'] as const,
  closed: (outcome: 'won' | 'lost') => ['deals', 'closed', outcome] as const,
  forRecord: (type: 'person' | 'company', id: string) => ['deals', 'for', type, id] as const,
  detail: (id: string) => ['deal', id] as const,
  lookup: (personId?: string, companyId?: string) => ['deals', 'lookup', personId ?? null, companyId ?? null] as const,
  pick: (type: 'company' | 'person', q: string, companyId?: string | null) => ['deals', 'pick', type, q, companyId ?? null] as const,
  fields: ['fields', 'deals'] as const,
};

/** The colour behind a stage dot or swatch. */
export function stageSwatch(color: StageColor | string): string {
  return ['lead', 'qual', 'prop', 'nego', 'won', 'lost'].includes(color) ? `var(--st-${color})` : `var(--${color})`;
}

export const STAGE_COLOR_NAMES: Record<StageColor, string> = {
  lead: 'Sky',
  qual: 'Teal',
  prop: 'Amber',
  nego: 'Coral',
  info: 'Blue',
  accent: 'Orange',
  warning: 'Gold',
  won: 'Green',
  lost: 'Grey',
};

const weekdayFmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });

export interface NextStepLabel {
  text: string;
  /** The due part on its own, such as "today 11:00". */
  when: string | null;
  state: NextStepState | null;
}

/** "Send revised SOW · today 11:00", "Call Sven · overdue since Wed", "Share case study · Tue 20 Oct". */
export function nextStepLabel(
  d: { nextStep: string | null; nextStepAt: string | null; nextStepHasTime: boolean },
  now: Date = new Date(),
): NextStepLabel | null {
  if (!d.nextStep) return null;
  if (!d.nextStepAt) return { text: d.nextStep, when: null, state: null };
  const at = new Date(d.nextStepAt);
  const state = nextStepState(at, now);
  let when: string;
  if (state === 'overdue') {
    when = daysFromToday(at, now) >= -6 ? `overdue since ${weekdayFmt.format(at)}` : `overdue since ${formatDate(at)}`;
  } else if (state === 'today') {
    when = d.nextStepHasTime ? `today ${formatTime(at)}` : 'today';
  } else if (daysFromToday(at, now) === 1) {
    when = d.nextStepHasTime ? `tomorrow ${formatTime(at)}` : 'tomorrow';
  } else {
    when = formatDay(at);
  }
  return { text: `${d.nextStep} · ${when}`, when, state };
}

/** "Today", "Yesterday", "4 days ago", then the date. */
export function activityLabel(at: string, now: Date = new Date()): string {
  const days = -daysFromToday(at, now);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return formatDate(at);
}

export interface StaleInfo {
  days: number;
  label: string;
  tip: string;
}

export function staleInfo(lastActivityAt: string, staleAfterDays: number, now: Date = new Date()): StaleInfo | null {
  const days = staleDays(lastActivityAt, staleAfterDays, now);
  if (days === null) return null;
  return { days, label: `Stale · ${days} d`, tip: `No activity for ${days} days. Stale after ${staleAfterDays}.` };
}

export function firstName(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

/** "Kestrel Bank · Mobile onboarding audit", or the deal name alone when it has no company. */
export function dealTitle(d: { name: string; company: { name: string } | null }): string {
  return d.company ? `${d.company.name} · ${d.name}` : d.name;
}

/** Short-lived board state other modules set: the card that just landed somewhere new. */
export const boardUi = createStore<{ justMoved: string | null }>({ justMoved: null });

let justMovedTimer: ReturnType<typeof setTimeout> | undefined;

export function markJustMoved(id: string): void {
  clearTimeout(justMovedTimer);
  boardUi.set({ justMoved: id });
  justMovedTimer = setTimeout(() => boardUi.set({ justMoved: null }), 900);
}

/** Reads an amount typed into a form: "48,000", "48 000", "€1250.50". Empty is zero; anything else that is not a number is NaN. */
export function parseAmount(text: string): number {
  const t = text.replace(/[\s,\u00a0\u202f]/g, '').replace(/^[€£$]/, '');
  if (!t) return 0;
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : NaN;
}

/** The date and time boxes for a next step's due date. */
export function nextStepInputs(at: string | null, hasTime: boolean): { date: string; time: string } {
  if (!at) return { date: '', time: '' };
  const d = new Date(at);
  return { date: toDateInput(d), time: hasTime ? toTimeInput(d) : '' };
}

/** The due date the server takes: an exact moment, a whole day (YYYY-MM-DD), or nothing. */
export function nextStepPayload(date: string, time: string): { nextStepAt: string | null; nextStepHasTime: boolean } {
  if (!date) return { nextStepAt: null, nextStepHasTime: false };
  if (!time) return { nextStepAt: date, nextStepHasTime: false };
  return { nextStepAt: new Date(`${date}T${time}`).toISOString(), nextStepHasTime: true };
}
